import { motion } from "framer-motion";
import { FlaskConical, Gem, Sparkles, BadgeCheck, CloudRain, Wine } from "lucide-react";

const pillars = [
  {
    icon: FlaskConical,
    title: "Alkaline pH of 8–8.5",
    desc: "Achieved through precise mineral enrichment and carefully maintained — a defining product characteristic, stated exactly as it is.",
  },
  {
    icon: Gem,
    title: "Balanced Mineral Profile",
    desc: "Magnesium and Calcium in careful, precise ratios — the same minerals found in celebrated mountain springs.",
  },
  {
    icon: Sparkles,
    title: "Clean, Smooth Taste",
    desc: "The mineral balance shapes a soft, rounded character — water that tastes refined from the first sip to the last.",
  },
  {
    icon: BadgeCheck,
    title: "Controlled Purification & Quality",
    desc: "UV light, fine filtration, activated carbon and ozonation — with every batch verified for pH stability, mineral balance and seal integrity.",
  },
  {
    icon: CloudRain,
    title: "Sustainable Atmospheric Origin",
    desc: "Gathered from atmospheric moisture, never drawn from the ground — every bottle leaves wells full and rivers flowing.",
  },
  {
    icon: Wine,
    title: "Premium Glass Presentation",
    desc: "Bottled in glass, designed for the table — a presentation worthy of the water inside.",
  },
];

export const HealthBenefits = () => (
  <section data-testid="health-benefits-section" className="py-24 md:py-32 lg:py-40 bg-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
      >
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">
          The Nyla Standard
        </p>
        <h2
          data-testid="health-benefits-heading"
          className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]"
        >
          Six things every bottle stands for.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
          Nyla is a premium, air-crafted drinking water. These are the characteristics we hold
          ourselves to — stated plainly, measured precisely.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {pillars.map((b, i) => (
          <motion.div
            key={b.title}
            data-testid={`health-benefit-${i}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
            className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300 ease-out"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#2C3E35]/10 flex items-center justify-center mb-5">
              <b.icon className="w-6 h-6 text-[#2C3E35]" strokeWidth={1.6} />
            </div>
            <h3 className="font-display text-lg md:text-xl tracking-tight font-medium text-[#1D1D1F] mb-2">
              {b.title}
            </h3>
            <p className="text-sm leading-relaxed text-[#86868B]">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
